import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  Link as MUILink,
  Stack,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import Close from "@mui/icons-material/Close";

import useResponsive from "@/hooks/useResponsive";
import { PRIVACY_POLICY_URL } from "@/utils/cookieConsent";

interface CookiePreferencesModalProps {
  open: boolean;
  initialAnalytics: boolean;
  initialMarketing: boolean;
  onClose: () => void;
  onSave: (settings: { analytics: boolean; marketing: boolean }) => void;
}

interface CategoryRowProps {
  title: string;
  description: string;
  alwaysActive?: boolean;
  checked: boolean;
  onChange?: (checked: boolean) => void;
}

const CategoryRow = ({
  title,
  description,
  alwaysActive,
  checked,
  onChange,
}: CategoryRowProps) => {
  const theme = useTheme();
  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 0.5 }}
      >
        <Typography
          variant="subtitle1"
          component="p"
          fontWeight={700}
          color="text.primary"
        >
          {title}
        </Typography>
        {alwaysActive ? (
          <Chip
            label="Always Active"
            size="small"
            sx={{
              backgroundColor: "#E7F7EC",
              color: "#1F9254",
              fontWeight: 700,
              height: 24,
            }}
          />
        ) : (
          <Switch
            checked={checked}
            onChange={(_, v) => onChange?.(v)}
            inputProps={{ "aria-label": `${title} toggle` }}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: theme.palette.common.white,
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: theme.palette.primary.main,
                opacity: 1,
              },
            }}
          />
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
  );
};

const CookiePreferencesModal = ({
  open,
  initialAnalytics,
  initialMarketing,
  onClose,
  onSave,
}: CookiePreferencesModalProps) => {
  const theme = useTheme();
  const isMobile = useResponsive("down", "sm");
  const [analytics, setAnalytics] = useState(initialAnalytics);
  const [marketing, setMarketing] = useState(initialMarketing);

  useEffect(() => {
    if (open) {
      setAnalytics(initialAnalytics);
      setMarketing(initialMarketing);
    }
  }, [open, initialAnalytics, initialMarketing]);

  const handleSave = () => {
    onSave({ analytics, marketing });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      fullScreen={isMobile}
      aria-labelledby="cookie-settings-title"
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          p: { xs: 2.5, sm: 3 },
          m: { xs: 0, sm: 2 },
          maxWidth: { sm: 400 },
        },
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Typography
          id="cookie-settings-title"
          variant="h5"
          component="h2"
          color="text.primary"
        >
          Cookie Settings
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close cookie settings"
          sx={{ color: theme.palette.grey[600] }}
        >
          <Close />
        </IconButton>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        We use cookies to improve your experience and for analytics. You can
        choose which categories of cookies you want to allow.
      </Typography>

      <Stack spacing={2.5} sx={{ mb: 3 }}>
        <CategoryRow
          title="Essential Cookies"
          description="These cookies are necessary for the website to function and cannot be switched off."
          alwaysActive
          checked
        />
        <CategoryRow
          title="Analytics Cookies"
          description="Help us understand how visitors interact with our website by collecting anonymous information."
          checked={analytics}
          onChange={setAnalytics}
        />
        <CategoryRow
          title="Marketing Cookies"
          description="Used to deliver relevant ads and measure the effectiveness of marketing campaigns."
          checked={marketing}
          onChange={setMarketing}
        />
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          onClick={handleSave}
        >
          Save Preferences
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          size="large"
          fullWidth
          onClick={onClose}
        >
          Cancel
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Learn more in our{" "}
        <MUILink
          href={PRIVACY_POLICY_URL}
          target="_blank"
          rel="noopener noreferrer"
          underline="always"
          sx={{ color: theme.palette.primary.main, fontWeight: 700, textDecoration: "underline" }}
        >
          Privacy Policy
        </MUILink>
      </Typography>
    </Dialog>
  );
};

export default CookiePreferencesModal;
